import { useMemo } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { api, queryClient } from 'config/api'

const PENDING_APPEALS_COUNT_QUERY_KEY = 'grade-appeals-pending-count'

export function refreshPendingAppealsCount() {
  return queryClient.invalidateQueries({
    queryKey: [PENDING_APPEALS_COUNT_QUERY_KEY],
  })
}

/**
 * Pending appeals per section across every section the instructor can review,
 * in a single request. The nav badge reads `total`; the dashboard's section
 * filter reads `bySection`. Both share this one query's cache, so opening the
 * page does not refetch what the nav already loaded.
 *
 * The server omits sections with no pending appeals (GROUP BY yields only the
 * non-zero groups), so `bySection.get(id) ?? 0` fills the zeros the dropdown
 * needs to show every section.
 */
export function usePendingAppealsCount(sectionIds: Array<string>) {
  // Sorted so the cache key is stable regardless of the order sections stream
  // in from Firestore.
  const sortedIds = useMemo(() => [...sectionIds].sort(), [sectionIds])

  const { data } = useQuery({
    queryKey: [PENDING_APPEALS_COUNT_QUERY_KEY, sortedIds],
    queryFn: async () => {
      const [result, error] = await api.v1.gradeAppeals
        .pendingCount({ body: { sectionIds: sortedIds } })
        .safe()
      if (error) throw error
      return result
    },
    enabled: sortedIds.length > 0,
    staleTime: 30_000,
    // A failed background refetch shouldn't zero the badge — keep the last known
    // counts rather than falling back to 0 (which reads as "no pending appeals").
    placeholderData: keepPreviousData,
  })

  return useMemo(() => {
    const bySection = new Map<string, number>()
    let total = 0
    for (const section of data?.sections ?? []) {
      bySection.set(section.sectionId, section.pending)
      total += section.pending
    }
    return { total, bySection }
  }, [data])
}
